/* eslint-disable tailwindcss/no-arbitrary-value */
'use client';
import { usePinnedBottomHeight } from '@hyphengroup/cms-components/hooks/usePinnedBottomHeight';
import { MaterialIconName } from '@hyphengroup/cms-components/types/materialIconName';
import classNames from 'classnames';
import { MaterialIcon } from 'components/modulab/MaterialIcon';
import { useBreakpoint } from 'hooks/useBreakpoint';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ComponentAnchorStickyFooter } from '$/(cms)/_modules/cmsService/types';
import { pushDataLayer } from '$/components/cms/pushDataLayer';
import { useClientContext } from '$/modules/context/client';

const SCROLL_DEBOUNCE_MS = 75;
const SCROLL_TRIGGER_OFFSET = 10;

type ResolvedItem = {
  id: string;
  anchorId: string;
  targetId: string;
  label: string;
  iconName?: MaterialIconName;
  index: number;
};

export const resolveAnchorTargetId = (anchorId: string): string => {
  const trimmed = anchorId.trim();
  if (!trimmed) return '';
  if (/^content-\d+$/.test(trimmed)) return trimmed;
  const match = trimmed.match(/_(\d+)$/);
  if (match) return `content-${match[1]}`;
  return trimmed;
};

const getHeaderOffset = (): number => {
  const candidates = Array.from(
    document.querySelectorAll<HTMLElement>('header, nav, .sticky, .fixed'),
  );
  if (!candidates.length) return 0;

  const centerX = window.innerWidth / 2;
  const topStack = candidates
    .map((element) => ({
      element,
      rect: element.getBoundingClientRect(),
      position: window.getComputedStyle(element).position,
    }))
    .filter(
      ({ rect, position }) =>
        (position === 'fixed' || position === 'sticky') &&
        rect.bottom > 0 &&
        rect.top < window.innerHeight &&
        rect.left <= centerX &&
        rect.right >= centerX,
    )
    .sort((a, b) => a.rect.top - b.rect.top);

  if (!topStack.length) return 0;

  let coverage = 0;
  topStack.forEach(({ rect }) => {
    if (rect.top <= coverage + 1) {
      coverage = Math.max(coverage, rect.bottom);
    }
  });

  return Math.max(0, coverage);
};

export const AnchorStickyFooterComponent: React.FC<
  ComponentAnchorStickyFooter
> = ({ visible = true, icon, items }) => {
  const { brand } = useClientContext();
  const viewportBreakpoint = useBreakpoint({
    _: '_',
    sm: 'sm',
    md: 'md',
    lg: 'lg',
    xl: 'xl',
    xxl: 'xxl',
  });
  const [selectedTargetId, setSelectedTargetId] = useState<string | null>(null);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);
  const [headerOffset, setHeaderOffset] = useState(0);
  const [bar, setBar] = useState<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const debounceRef = useRef<number | null>(null);
  const selectedRef = useRef<string | null>(null);
  const intersectingIdsRef = useRef<Set<string>>(new Set());
  const autoScrollRef = useRef(false);
  const autoScrollTimeoutRef = useRef<number | null>(null);

  const iconEnabled = Boolean(icon);

  const resolvedItems = useMemo<ResolvedItem[]>(() => {
    return (items ?? [])
      .filter(Boolean)
      .map((item, index) => ({
        id: item?.id ?? `${index}`,
        anchorId: item?.anchor_to?.trim() ?? '',
        targetId: resolveAnchorTargetId(item?.anchor_to?.trim() ?? ''),
        label: item?.label?.trim() ?? '',
        iconName: iconEnabled ? item?.material_icon : undefined,
        index,
      }))
      .filter((item) => item.anchorId && item.targetId && item.label);
  }, [iconEnabled, items]);

  useEffect(() => {
    selectedRef.current = selectedTargetId;
  }, [selectedTargetId]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduceMotion(mediaQuery.matches);
    update();
    mediaQuery.addEventListener('change', update);
    return () => mediaQuery.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleScroll = () => {
      setHasScrolled(
        (window.scrollY || document.documentElement.scrollTop) >
          SCROLL_TRIGGER_OFFSET,
      );
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const settleAutoScroll = () => {
      if (!autoScrollRef.current) return;
      if (autoScrollTimeoutRef.current) {
        window.clearTimeout(autoScrollTimeoutRef.current);
      }
      autoScrollTimeoutRef.current = window.setTimeout(() => {
        autoScrollRef.current = false;
        autoScrollTimeoutRef.current = null;
      }, 150);
    };

    window.addEventListener('scroll', settleAutoScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', settleAutoScroll);
      if (autoScrollTimeoutRef.current) {
        window.clearTimeout(autoScrollTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    let rafId = 0;

    const updateOffset = () => setHeaderOffset(getHeaderOffset());
    const scheduleUpdate = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        updateOffset();
      });
    };

    updateOffset();
    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);

    return () => {
      window.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!('IntersectionObserver' in window)) return;
    if (!resolvedItems.length) return;

    const anchors = resolvedItems
      .map((item) => {
        const element = document.getElementById(item.targetId);
        if (!element) {
          console.warn(
            `AnchorStickyFooter: missing anchor target for ${item.targetId}`,
          );
          return null;
        }
        return element;
      })
      .filter(Boolean) as HTMLElement[];

    if (!anchors.length) return;

    observerRef.current?.disconnect();
    intersectingIdsRef.current.clear();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (autoScrollRef.current) return;
        entries.forEach((entry) => {
          const entryId = entry.target.id;
          if (!entryId) return;
          if (entry.isIntersecting) {
            intersectingIdsRef.current.add(entryId);
          } else {
            intersectingIdsRef.current.delete(entryId);
          }
        });

        const inView = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => {
            const topA = Math.abs(a.boundingClientRect.top);
            const topB = Math.abs(b.boundingClientRect.top);
            if (topA === topB) {
              return b.intersectionRatio - a.intersectionRatio;
            }
            return topA - topB;
          });
        if (!inView.length) {
          if (!intersectingIdsRef.current.size && selectedRef.current) {
            setSelectedTargetId(null);
          }
          return;
        }

        const nextTargetId = inView[0].target.id;
        if (!nextTargetId || selectedRef.current === nextTargetId) return;

        if (debounceRef.current) {
          window.clearTimeout(debounceRef.current);
        }
        debounceRef.current = window.setTimeout(() => {
          const nextItem = resolvedItems.find(
            (item) => item.targetId === nextTargetId,
          );
          if (!nextItem || selectedRef.current === nextTargetId) return;

          setSelectedTargetId(nextTargetId);
          pushDataLayer({
            event: 'anchor-footer.autoselect',
            label: nextItem.label,
            anchorId: nextItem.anchorId,
            index: nextItem.index,
          });
        }, SCROLL_DEBOUNCE_MS);
      },
      {
        rootMargin: `${-headerOffset}px 0px 0px 0px`,
        threshold: [0, 0.25, 0.5, 0.75, 1],
      },
    );

    anchors.forEach((anchor) => observerRef.current?.observe(anchor));

    return () => {
      observerRef.current?.disconnect();
      // eslint-disable-next-line react-hooks/exhaustive-deps
      intersectingIdsRef.current?.clear();
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
      }
    };
  }, [resolvedItems, headerOffset]);

  const showBar = hasScrolled || Boolean(selectedTargetId);
  // What the bar covers while it is slid in, for the Sort & Filter button and
  // rail to stay clear of it.
  usePinnedBottomHeight(bar, showBar);

  if (!visible || !resolvedItems.length) return null;

  const handleItemClick = (item: ResolvedItem) => {
    const target = document.getElementById(item.targetId);
    if (!target) {
      console.warn(
        `AnchorStickyFooter: missing anchor target for ${item.targetId}`,
      );
      return;
    }
    autoScrollRef.current = true;
    const offset = headerOffset || getHeaderOffset();
    const top = target.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({
      top,
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
    setSelectedTargetId(item.targetId);
    pushDataLayer({
      event: 'anchor-footer.click',
      page: window.location.pathname,
      brand,
      label: item.label,
      anchorId: item.anchorId,
      index: item.index,
      iconEnabled,
      viewportBreakpoint,
    });
  };

  return (
    <div
      ref={setBar}
      className={classNames(
        'group fixed bottom-0 left-0 z-[60] w-full shadow-[0px_0px_12px_0px_rgba(0,0,0,0.15)] backdrop-blur-sm transition-all duration-300 ease-out bg-white/80 lg:hover:bg-white',
        {
          'translate-y-0': showBar,
          'translate-y-full': !showBar,
        },
      )}
      aria-hidden={!showBar}
    >
      <div className="w-full px-4 pb-[calc(env(safe-area-inset-bottom)+12px)] pt-3 md:py-4 lg:px-6">
        <div className="mx-auto max-w-[--breakpoints-content-narrow-3-lg]">
          <div
            className={classNames(
              'flex w-full items-stretch rounded-[6px] border border-buttons/base p-[2px] transition-all bg-transparent md:rounded-[8px] md:p-1',
              {
                'lg:group-hover:border-borders/default': selectedTargetId,
              },
            )}
          >
            {resolvedItems.map((item) => {
              const isSelected = selectedTargetId === item.targetId;
              const fillClass = isSelected
                ? 'bg-fill/card lg:group-hover:bg-fill/primary-light'
                : 'bg-transparent lg:hover:bg-fill/alternative-2';

              return (
                <button
                  key={item.id}
                  type="button"
                  className={classNames(
                    'group flex min-w-0 flex-1 items-center justify-center rounded-[6px] p-[2px] text-center transition-colors duration-150 md:rounded-[8px] md:px-1 md:py-[6px]',
                    'focus:outline-none focus-visible:outline-dashed focus-visible:outline-primary',
                    fillClass,
                  )}
                  aria-pressed={isSelected}
                  onClick={() => handleItemClick(item)}
                >
                  <span
                    className={classNames('flex min-w-0 items-center', {
                      'flex-col gap-px md:gap-spacing/gap/soft-4px lg:flex-row lg:gap-spacing/gap/soft-4px':
                        iconEnabled,
                      'justify-center': !iconEnabled,
                    })}
                  >
                    {iconEnabled && item.iconName && (
                      <MaterialIcon
                        icon={item.iconName}
                        className="text-buttons/neutral-dark text-[18px] md:text-[24px]"
                      />
                    )}
                    <span className="md:mhg-alpha-typography-button-1 min-w-0 truncate text-center font-font-weight/button-1 text-buttons/neutral-dark text-[12px] leading-[16px]">
                      {item.label}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
